import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import useLocationActions from '@app/hooks/locations/useLocationActions';
import useLocations from '@app/hooks/locations/useLocations';
import useModal from '@app/hooks/useModal';
import type { Location, LocationListParams } from '@app/services/api.types';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import { Flex, type TableColumnsType } from 'antd';
import React, { useState } from 'react';
import { CgSoftwareDownload } from 'react-icons/cg';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';
import { MdAdd } from 'react-icons/md';
import { Link } from 'react-router';
import { LocationAddModal } from './components/LocationAddModal';
import LocationDeleteModal from './components/LocationDeleteModal';
import { LocationEditModal } from './components/LocationEditModal';
import LocationFilters from './components/LocationFilters';
import useMe from '@app/hooks/auth/useMe';
import LocationDrawer from './components/LocationDrawer';

export const LocationsList = () => {
    const [selectedRecord, setSelectedRecord] = useState<Location | null>(null);
    const { exportLocations } = useLocationActions();
    const [params, setParams] = useState<LocationListParams>({
        page: 1,
        per_page: 10,
        search: '',
        brand_id: [],
        channel_id: [],
        order_by: undefined,
        order_dir: undefined,
    });
    const { data: me } = useMe();

    const modalAdd = useModal<Location>({
        initialData: undefined,
    });

    const modalEdit = useModal<Location>({
        initialData: undefined,
    });

    const modalRemove = useModal<Location>({
        initialData: undefined,
    });

    const { data, isLoading } = useLocations(params);

    const columns: TableColumnsType<Location> = [
        {
            title: 'Location Name',
            dataIndex: 'name',
            key: 'name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Brand',
            dataIndex: 'brand_name',
            key: 'brand_name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Channel Partner',
            dataIndex: 'channel_name',
            key: 'channel_name',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Admin Email',
            dataIndex: 'contact_email',
            key: 'contact_email',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Admin Phone',
            dataIndex: 'contact_telephone',
            key: 'contact_telephone',
            render: (value) => value || '-',
            sorter: true,
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            sorter: true,
            width: 150,
            fixed: 'right',
            hidden: !me?.is_internal,
            render: (_value, record) => (
                <StyledButtonGroup justify="flex-start">
                    <StyledButton
                        icon={<HiOutlinePencil />}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            openEditModal(record);
                        }}>
                        Edit
                    </StyledButton>
                    <StyledButton
                        icon={<HiOutlineTrash />}
                        color="red"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            modalRemove.open(record);
                        }}>
                        Delete
                    </StyledButton>
                </StyledButtonGroup>
            ),
        },
    ];

    const openEditModal = (record: Location) => {
        modalEdit.open(record);
    };

    const closeAddModal = () => {
        modalAdd.reset();
    };

    const closeEditModal = () => {
        modalEdit.reset();
    };

    const closeRemoveModal = () => {
        modalRemove.reset();
    };

    const handleExport = async () => {
        exportLocations.mutate(params);
    };

    return (
        <React.Fragment>
            <PageMeta title="Locations" />

            <PageHeader
                breadcrumbItems={[
                    {
                        title: <Link to={url.adminPortal.dashboard}>Dashboard</Link>,
                    },
                    { title: 'Locations' },
                ]}>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="Locations" />
                        <PageIntro>
                            <p>Manage office locations</p>
                        </PageIntro>
                    </div>
                    {me?.is_internal ? (
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    color="blue"
                                    icon={<MdAdd size={20} />}
                                    onClick={() => modalAdd.open()}>
                                    Add New
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    ) : null}
                </Flex>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <LocationFilters
                            onChange={(values) => {
                                setParams((prev) => ({ ...prev, ...values, page: 1 }));
                            }}
                        />
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    icon={<CgSoftwareDownload size={20} />}
                                    disabled={Boolean(!data?.total)}
                                    onClick={handleExport}>
                                    Export CSV
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<Location>
                        columns={columns}
                        dataSource={data?.data ?? []}
                        rowKey={({ id }) => id}
                        // scroll={{ x: 860 }}
                        loading={isLoading}
                        pagination={{
                            current: params.page,
                            pageSize: params.limit,
                            total: data?.total ?? 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                order_by,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedRecord(record),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />
                </PageLayout.Content>
            </PageLayout>

            <LocationDrawer
                size={700}
                onClose={() => setSelectedRecord(null)}
                open={Boolean(selectedRecord)}
                data={selectedRecord}
            />

            <LocationAddModal
                open={modalAdd.isOpen}
                onCancel={closeAddModal}
                onOk={closeAddModal}
            />

            <LocationEditModal
                open={modalEdit.isOpen}
                data={modalEdit.data}
                onCancel={closeEditModal}
                onOk={closeEditModal}
            />

            <LocationDeleteModal
                open={modalRemove.isOpen}
                data={modalRemove.data}
                onCancel={closeRemoveModal}
                onOk={closeRemoveModal}
            />
        </React.Fragment>
    );
};

export default LocationsList;
